import {
	Component,
	EventEmitter,
	Input,
	OnChanges,
	OnInit,
	Output,
	ViewEncapsulation,
} from '@angular/core';
import {
	CustomerDTO,
	DATE_MDY,
	DATE_YMD,
	DropdownItem,
	GET_LOAD_VOLUME,
	GET_PROJECTED_MARGIN,
	IS_EMPTY,
	IS_NOT_EMPTY,
	IS_NOT_NULL_OR_UNDEFINED,
	IS_NULL_OR_UNDEFINED,
	Owner,
	ProjectDTO,
	USERS_LIST_FOR_POPOVER,
} from 'cargo-chief-commons';
import { Cargochief } from '@service/cargochief/cargochief.service';
import { ACCOUNT_STATUS, TOOLTIP_MSG } from '@domain/constants/app-variables';
import { AccessCacheService } from '@service/access-cache/access-cache.service';
import { UntypedFormControl, Validators } from '@angular/forms';
import { ProjectHelperService } from '@service/projectHelper/project-helper.service';
import { AccountSettingsService } from '@service/accountSettings/account-settings.service';
import { UtilService } from '@service/util/util.service';
import { BaConfigService } from '@service/baConfig/ba-config.service';

import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { environment } from '@environment/environment';
import { PROJECT_TYPES } from '@domain/constants/project-types';
import { MatCheckboxChange } from '@angular/material/checkbox';

@Component({
	selector: 'app-project-specifications',
	templateUrl: './project-specifications.component.html',
	styleUrls: ['./project-specifications.component.scss'],
	encapsulation: ViewEncapsulation.None,
})
export class ProjectSpecificationsComponent implements OnInit, OnChanges {
	@Input() project: ProjectDTO = null;
	@Input() isUpdatingProject: boolean = false;

	@Output() updateProjectEvent: EventEmitter<ProjectDTO> = new EventEmitter();

	usersList: Array<Owner> = null;
	projectCopy: ProjectDTO = null;
	minimumDueDate = new Date();
	dueDate = new UntypedFormControl(null, Validators.required);
	PROJECT_STAGES: Array<any> = [];
	loadVolumeDuration: Array<string> = [];
	daysRun: string = '';
	targetMargin: number = null;
	additionalNotes: string[] = [];
	showNotes: boolean = false;
	DAYS: Array<DropdownItem> = null;
	DAYS_RUN: Array<string> = [];
	IS_TEAMS_CAPACITY_ENABLED: boolean = false;
	IS_NOT_NULL_OR_UNDEFINED = IS_NOT_NULL_OR_UNDEFINED;
	IS_NULL_OR_UNDEFINED = IS_NULL_OR_UNDEFINED;
	IS_NOT_EMPTY = IS_NOT_EMPTY;
	IS_EMPTY = IS_EMPTY;
	GET_PROJECTED_MARGIN = GET_PROJECTED_MARGIN;
	TOOLTIP_MSG = TOOLTIP_MSG;
	projectEquipments: string[] = [];
	trailerTypes: any[] = [];
	projectTypes: any = PROJECT_TYPES;
	projectType: any;
	PROJECT_TYPES = PROJECT_TYPES;
	private debouncer: Subject<any> = new Subject<any>();
	private targetMarginChanged: Subject<any> = new Subject<any>();
	isWaterfallOrDedicatedLaneProject: boolean = false;

	// variables for component state
	isUserRoleAdmin: boolean = false;
	isUserProject: boolean = false;
	userPopoverComponent = {
		placeholder: '',
		selectedUsers: [],
		enabled: false,
	};

	constructor(
		public cargochief: Cargochief,
		public accountSettings: AccountSettingsService,
		public projectHelperService: ProjectHelperService,
		private accessCache: AccessCacheService,
		public utilService: UtilService,
		public baConfigService: BaConfigService
	) {
		this.trailerTypes = this.accountSettings.SEARCHABLE_TRAILER_TYPES;
		this.loadVolumeDuration = this.projectHelperService.getLoadVolumeDuration();
		this.IS_TEAMS_CAPACITY_ENABLED =
			environment.TEAMS_CAPACITY && this.accountSettings.TEAMS_CAPACITY;
		this.baConfigService.setBaConfig();
	}

	ngOnInit(): void {
		if (IS_NOT_NULL_OR_UNDEFINED(this.project)) {
			this.PROJECT_STAGES = this.projectHelperService.getProjectStages();
			this.DAYS = this.projectHelperService.days;
			this.initialSetup();
			this.calculateTargetMargin(this.project.maxRate, this.project.minRate);

			this.debouncer.pipe(debounceTime(1000)).subscribe((value) => {
				switch (value) {
					case 'minRate':
						this.maxBuyChanged();
						break;
					case 'maxRate':
						this.customerRateChanged();
						break;
					case 'packaging':
					case 'trailerRequirements':
					case 'shipperNotes':
					case 'receiverNotes':
						this.updateProject();
						break;
				}
			});

			this.targetMarginChanged.pipe(debounceTime(250)).subscribe((value) => {
				this.calculateMarginChange();
			});
		}

		this.isWaterfallOrDedicatedLaneProject =
			this.project?.type == PROJECT_TYPES.WATERFALL.value ||
			this.project?.type == PROJECT_TYPES.DEDICATED_LANE.value;

		this.isUserRoleAdmin = this.utilService.isUserRoleAdmin();
	}

	ngOnChanges() {
		this.initialSetup();
		if (IS_NOT_NULL_OR_UNDEFINED(this.projectCopy)) {
			this.isUserProject = this.cargochief.USER_ID == this.projectCopy.userId;
			this.userPopoverComponent.placeholder =
				IS_NULL_OR_UNDEFINED(this.projectCopy.assigneeId) &&
				this.cargochief.USER_ID != this.projectCopy.userId
					? 'Unassigned'
					: 'Assign User';
			this.userPopoverComponent.selectedUsers = IS_NULL_OR_UNDEFINED(this.projectCopy.assigneeId)
				? []
				: [this.projectCopy.assigneeId];
			this.userPopoverComponent.enabled = this.isUserProject || this.isUserRoleAdmin;
		}
	}

	initialSetup() {
		this.populateNotesMetadata();
		this.DAYS_RUN = GET_LOAD_VOLUME(this.project).split(', ');
		this.dueDate = new UntypedFormControl(
			new Date(DATE_MDY(this.project.dueDate)),
			Validators.required
		);
		this.projectCopy = { ...this.project };
		this.projectEquipments = this.projectCopy.equipmentType?.split(',') || [];
		this.usersList = USERS_LIST_FOR_POPOVER({
			users: this.accessCache.getAccountUsers(),
			assigneeId: this.project.assigneeId,
			removeHavingStatus: ACCOUNT_STATUS.INACTIVE,
		});
	}

	isProjectUpdated(): boolean {
		return (
			(IS_NOT_NULL_OR_UNDEFINED(this.project) &&
				IS_NOT_NULL_OR_UNDEFINED(this.projectCopy) &&
				(this.project.name != this.projectCopy.name ||
					this.project.customerDTO?.name != this.projectCopy.customerDTO?.name ||
					this.project.assigneeId != this.projectCopy.assigneeId ||
					DATE_MDY(this.project.dueDate) != DATE_MDY(this.projectCopy.dueDate) ||
					this.project.stageId != this.projectCopy.stageId ||
					this.project.loadVolumeDuration != this.projectCopy.loadVolumeDuration ||
					this.project.minRate != this.projectCopy.minRate ||
					this.project.maxRate != this.projectCopy.maxRate ||
					(IS_NOT_EMPTY(this.projectCopy.loadCount) &&
						this.project.loadCount != this.projectCopy.loadCount) ||
					(IS_NOT_EMPTY(this.projectCopy.commodity) &&
						this.project.commodity != this.projectCopy.commodity) ||
					(IS_NOT_EMPTY(this.projectCopy.weight) &&
						this.project.weight != this.projectCopy.weight) ||
					(IS_NOT_EMPTY(this.projectCopy.loadVolume) &&
						this.project.loadVolume != this.projectCopy.loadVolume) ||
					(this.projectEquipments.join(',') != this.projectCopy.equipmentType &&
						this.projectEquipments.length > 0) ||
					this.project.inviteUngroupedCarriers != this.projectCopy.inviteUngroupedCarriers ||
					this.project.type != this.projectCopy.type ||
					this.project.revenueCode != this.projectCopy.revenueCode ||
					this.project.ungroupedCarriersDelay != this.projectCopy.ungroupedCarriersDelay ||
					this.project.maxBuy != this.projectCopy.maxBuy ||
					this.project.sendToOutOfNetworkCarriers != this.projectCopy.sendToOutOfNetworkCarriers ||
					this.project.sendToOutOfNetworkCarriersDelay !=
						this.projectCopy.sendToOutOfNetworkCarriersDelay)) ||
			this.project.requireTeams != this.projectCopy.requireTeams
		);
	}

	updateProject() {
		this.project = this.projectCopy;
		let projectDTO: any = Object.assign({}, this.projectCopy);
		projectDTO.dueDate = DATE_YMD(this.dueDate.value);
		projectDTO = this.projectHelperService.convertProjectDollarsToCents(projectDTO);
		projectDTO.equipmentType = this.projectEquipments.join(',');
		projectDTO = this.projectHelperService.updateProjectDaysRun(projectDTO, this.DAYS_RUN);
		projectDTO.sendToNetworkCarriersDelay = projectDTO.ungroupedCarriersDelay;
		projectDTO.sendToNetworkCarriers = projectDTO.inviteUngroupedCarriers;
		this.updateProjectEvent.emit(projectDTO);
	}

	setCustomer(selectedCustomer: CustomerDTO) {
		if (IS_NOT_NULL_OR_UNDEFINED(selectedCustomer) && IS_NOT_NULL_OR_UNDEFINED(this.projectCopy)) {
			this.projectCopy.customerDTO = selectedCustomer;
		}
	}

	assigneeUpdated(newAssignedUser: Owner) {
		if (IS_NOT_NULL_OR_UNDEFINED(newAssignedUser)) {
			this.projectCopy.assigneeId = newAssignedUser.userId;
		}
	}

	dueDateUpdated() {
		this.projectCopy.dueDate = this.dueDate.value;
	}

	inputChanged(propertyKey: string) {
		this.debouncer.next(propertyKey);
	}

	calculateMarginChange() {
		const minRate = this.projectCopy.minRate,
			maxRate = this.projectCopy.maxRate;
		if (
			(this.projectHelperService.isGrossMarginValid(this.targetMargin) &&
				this.projectHelperService.isValidRateInput(minRate) &&
				this.projectHelperService.isValidRateInput(maxRate)) ||
			(this.projectHelperService.isGrossMarginValid(this.targetMargin) &&
				this.projectHelperService.isValidRateInput(maxRate))
		) {
			this.projectCopy.minRate = Math.round(((100 - this.targetMargin) * Number(maxRate)) / 100);
		} else if (
			this.projectHelperService.isGrossMarginValid(this.targetMargin) &&
			this.projectHelperService.isValidRateInput(minRate)
		) {
			this.projectCopy.maxRate = Math.round(
				(Number(minRate) * 100) / (100 - Number(this.targetMargin))
			);
		}
	}

	maxBuyChanged() {
		const minRate = this.projectCopy.minRate,
			maxRate = this.projectCopy.maxRate,
			currentMargin = this.targetMargin;
		if (
			this.projectHelperService.isGrossMarginValid(currentMargin) &&
			this.projectHelperService.isValidRateInput(minRate) &&
			this.projectHelperService.isValidRateInput(maxRate)
		) {
			this.projectCopy.maxRate = Math.round((Number(minRate) * 100) / (100 - this.targetMargin));
		} else if (
			!this.projectHelperService.isGrossMarginValid(currentMargin) &&
			this.projectHelperService.isValidRateInput(minRate) &&
			this.projectHelperService.isValidRateInput(maxRate)
		) {
			this.calculateTargetMargin(maxRate, minRate);
		}
	}

	customerRateChanged() {
		const minRate = this.projectCopy.minRate,
			maxRate = this.projectCopy.maxRate,
			currentMargin = this.targetMargin;
		if (
			this.projectHelperService.isGrossMarginValid(currentMargin) &&
			this.projectHelperService.isValidRateInput(minRate) &&
			this.projectHelperService.isValidRateInput(maxRate)
		) {
			this.projectCopy.minRate = Math.round(((100 - this.targetMargin) * Number(maxRate)) / 100);
		} else if (
			!this.projectHelperService.isGrossMarginValid(currentMargin) &&
			this.projectHelperService.isValidRateInput(minRate) &&
			this.projectHelperService.isValidRateInput(maxRate)
		) {
			this.calculateTargetMargin(maxRate, minRate);
		}
	}

	calculateTargetMargin(maxRate: number, minRate: number) {
		if (IS_NOT_NULL_OR_UNDEFINED(maxRate) && IS_NOT_NULL_OR_UNDEFINED(minRate)) {
			const calculatedMargin = ((maxRate - minRate) / maxRate) * 100;
			if (calculatedMargin > 0) {
				this.targetMargin = Math.round(calculatedMargin);
			}
		}
	}

	onTargetMarginChange(projectCopy: ProjectDTO) {
		this.targetMarginChanged.next(projectCopy);
	}

	async submitLastBid() {
		let result = await this.projectHelperService.openLastBidDialog();
		if (IS_NOT_NULL_OR_UNDEFINED(result)) {
			this.projectCopy.lastBid = result.lastBid;
			this.updateProject();
		}
	}

	populateNotesMetadata() {
		this.additionalNotes = [];
		if (IS_NOT_EMPTY(this.project.packaging)) {
			this.additionalNotes.push('Packaging');
		}
		if (IS_NOT_EMPTY(this.project.trailerRequirements)) {
			this.additionalNotes.push('Trailer Requirements');
		}
		if (IS_NOT_EMPTY(this.project.shipperNotes)) {
			this.additionalNotes.push('Shipper Notes');
		}
		if (IS_NOT_EMPTY(this.project.receiverNotes)) {
			this.additionalNotes.push('Receiver Notes');
		}
	}

	sendOfferSettingUpdated(event: any, delayFieldName: string) {
		if (event) {
			this.projectCopy[delayFieldName] = 5;
		} else {
			this.projectCopy[delayFieldName] = 0;
		}
	}
}
